/**
 * The global abort gate for fire-and-forget waits (pWaitFor and similar).
 *
 * OWA-787: this used to be one AbortController for the life of the page. A
 * controller fires once, so after the App teardown aborted it (including React
 * StrictMode's development double mount), every later wait gated on the signal
 * rejected at once: polling's waits for the socket and for authentication
 * could never succeed again in that page.
 *
 * Aborting now arms a fresh controller. `globalAbortSignal` is a live ES module
 * binding, so code that reads it when it starts a wait (every current use
 * passes `{signal: globalAbortSignal}` inline) gets the current generation's
 * signal. Do not copy it into a module-level constant at import time.
 */
let controller = new AbortController();

export let globalAbortSignal: AbortSignal = controller.signal;

/** Reasons of the generations aborted so far, to recognise their rejections. */
const abortReasons = new Set<unknown>();

/**
 * Abort every wait gated on the global signal (App teardown, tests that
 * simulate logout), then arm a fresh controller so the next session's waits
 * can gate again.
 *
 * @param {unknown} [reason] - the abort reason the aborted waits reject with
 */
export function abortGlobalController(reason?: unknown): void {
    const aborting = controller;
    controller = new AbortController();
    globalAbortSignal = controller.signal;
    aborting.abort(reason);
    abortReasons.add(aborting.signal.reason);
}

/**
 * The controller as callers knew it: `globalController.abort()` aborts the
 * current generation and re-arms, and `signal` is the current generation's.
 */
export const globalController = {
    abort: (reason?: unknown) => abortGlobalController(reason),
    get signal(): AbortSignal {
        return globalAbortSignal;
    },
};

/**
 * Did this rejection come from a global abort (of this or an earlier
 * generation)?
 *
 * @param {unknown} err - the rejection value
 */
export function isGlobalAbort(err: unknown): boolean {
    if (globalAbortSignal.aborted) return true;
    if (abortReasons.has(err)) return true;
    return (err as {name?: string} | null)?.name === 'AbortError';
}

/**
 * Rejection handler for fire-and-forget waits on {@link globalAbortSignal}.
 *
 * `pWaitFor(..., {signal: globalAbortSignal})` rejects with the abort reason
 * when the app tears down. A chain that only has `.then(...)` turns that into
 * an *unhandled* rejection, which surfaces on `window.onunhandledrejection` and
 * in error reporting: noise that looks like a crash but is only teardown.
 * Waits that are still pending at teardown are the common case: several of
 * these poll with no timeout.
 *
 * Swallows the rejection only when it came from a global abort; anything else
 * (for example a pWaitFor timeout) is still reported, so real failures are not
 * hidden.
 */
export function ignoreGlobalAbort(err: unknown): void {
    if (isGlobalAbort(err)) return;
    console.debug('Wait rejected before the global abort fired:', err);
}

export function resetController() {
    abortGlobalController();
}
